/**
 * Pipeline types for parallel video-analysis paths.
 *
 * A pipeline TYPE is a code-defined analysis category (e.g. "anomaly", "bolo")
 * with its own prompt builder, params shape, queue, EventBridge rule, and at
 * least one ECS service. Adding a TYPE is a deploy.
 *
 * A pipeline INSTANCE is a runtime configuration of a TYPE for a specific
 * installation, stored in `VideoConfig.config.pipelines`. Adding an instance is
 * a config write (no deploy).
 *
 * Every Measurement, and every measurement-carrying EventBridge payload from
 * the VLM service onward, carries `pipelineType` and (for multi-instance types)
 * `pipelineLabel`. Events and AggregationWindows do NOT — they aggregate
 * across pipelines, so pipeline identity is not part of event identity. An
 * event instead records the BOLO watches it matched in `Event.watches`.
 *
 * See docs/multi-pipeline-architecture.md for the full architecture and the
 * recipe for adding a new pipeline type.
 */

import type { CameraRule } from "./video-config";

export const PIPELINE_TYPES = ["anomaly", "bolo"] as const;
export type PipelineType = (typeof PIPELINE_TYPES)[number];

/** Default pipeline type for legacy records written before multi-pipeline. */
export const DEFAULT_PIPELINE_TYPE: PipelineType = "anomaly";

/**
 * Anomaly pipeline params.
 *
 * Scope prompts (`installationContext`, `zoneContext`, `sensorContext`) are
 * RESOLVED fields populated by `VideoConfigService.applyTopLevelInheritance`
 * from the merged scope-level `analysisContext` values, with per-instance
 * overrides applied per section. The Python VLM worker reads these three
 * fields directly to build three structured sections in the Gemini prompt.
 *
 * The `*ContextOverride` fields are the per-pipeline-instance authored
 * overrides — set them to replace just one section's inherited value for
 * this pipeline. When unset, that section falls back to the scope-level
 * value. Clearing an override means deleting the key from `params`.
 */
export interface AnomalyParams {
	collisionFps?: number;
	enableCollisionEnhancement?: boolean;
	enableFireEnhancement?: boolean;
	enableThreatEnhancement?: boolean;
	enableVandalismEnhancement?: boolean;
	fireFps?: number;
	firstPassFps?: number;
	geminiModel?: string;
	/** Resolved installation-scope prompt (override or inherited). Populated by `applyTopLevelInheritance` at read time. */
	installationContext?: string;
	/** Per-instance override for the installation-scope prompt. Authored by users. */
	installationContextOverride?: string;
	/**
	 * The camera's enabled rules, resolved from the merged scope config by
	 * `applyTopLevelInheritance` at read time; never authored per instance.
	 * The first pass only flags which look met, so a flagged clip reaches
	 * verification, where the rules check decides.
	 */
	rules?: CameraRule[];
	/**
	 * Auto-generated physical description of the camera's permanent view,
	 * resolved from `Sensor.sceneDescription` at ingest (or supplied directly by
	 * an eval variant). The worker renders it as a NEUTRAL background block —
	 * deliberately NOT part of the `*Context` scope-prompt family, which the
	 * worker frames as directive "priority watch items". Mixing the two would
	 * turn a scene description into a hunt-list, which research showed drives
	 * false alerts up sharply. See sitescene#460.
	 */
	sceneDescription?: string;
	/** Per-instance override for the generated scene description. Authored by users. */
	sceneDescriptionOverride?: string;
	/** Resolved sensor-scope prompt (override or inherited). Populated by `applyTopLevelInheritance` at read time. */
	sensorContext?: string;
	/** Per-instance override for the sensor-scope prompt. Authored by users. */
	sensorContextOverride?: string;
	/**
	 * First-pass Gemini thinking level override (Gemini 3.x). When unset, the
	 * worker uses the pass-type default from `PASS_THINKING_LEVELS`.
	 */
	thinkingLevel?: "high" | "low" | "medium" | "minimal";
	/**
	 * Eval-only opt-in: resolve `sceneDescription` per job from the clip's own
	 * `EvalVideo.sceneDescription` rather than from this variant.
	 *
	 * Lives on the variant params because that is the only thing an eval author
	 * edits, but it never reaches the worker — `resolveVariantParams` consumes
	 * it when building each job's config. A scene description is per-camera and
	 * a variant spans cameras, so the flag is the only honest way to run a
	 * with-description arm across a multi-camera set.
	 */
	useSceneDescription?: boolean;
	vandalismFps?: number;
	weaponFps?: number;
	/** Resolved zone-scope prompt (override or inherited). Populated by `applyTopLevelInheritance` at read time. */
	zoneContext?: string;
	/** Per-instance override for the zone-scope prompt. Authored by users. */
	zoneContextOverride?: string;
}

/**
 * Top-level config keys that get lifted into a synthesized anomaly instance
 * when `config.pipelines` is absent. Shared by VideoConfigService
 * (synthesis) and the Python anomaly worker (field consumption).
 *
 * Note: scope prompts (`installationContext`/`zoneContext`/`sensorContext`)
 * are NOT in this list — they are resolved per-instance from `scopePrompts`
 * by `applyTopLevelInheritance`, not by lifting a single top-level field.
 */
export const LEGACY_ANOMALY_KEYS: readonly (keyof AnomalyParams)[] = [
	"collisionFps",
	"enableCollisionEnhancement",
	"enableFireEnhancement",
	"enableThreatEnhancement",
	"enableVandalismEnhancement",
	"fireFps",
	"firstPassFps",
	"geminiModel",
	"thinkingLevel",
	"vandalismFps",
	"weaponFps",
] as const;

/**
 * Top-level scope-merged config keys that each pipeline instance inherits as
 * defaults. An instance's own `params` always wins, so an instance can
 * override any inherited value (e.g. one BOLO instance picking a different
 * `geminiModel` than the installation default).
 *
 * Anomaly inherits all `LEGACY_ANOMALY_KEYS` — the historical top-level
 * fields the legacy VLM worker read directly. BOLO only inherits cross-type
 * fields like `geminiModel`; BOLO's own type-specific fields (`description`,
 * `ageRange`, `clothing`, ...) come exclusively from the instance.
 *
 * Non-VLM fields (`motionDetection`, `fisheye`, ...) are deliberately
 * excluded — they run upstream of the per-pipeline fan-out and stay
 * scope-merged only.
 *
 * See docs/multi-pipeline-architecture.md §5.
 */
export const INHERITABLE_PARAM_KEYS_BY_TYPE: Record<
	PipelineType,
	readonly string[]
> = {
	// `sceneDescription` is inheritable but not legacy: it is seeded onto the
	// merged config from `Sensor.sceneDescription` at ingest, then inherited by
	// each anomaly instance like any other top-level default.
	anomaly: [...LEGACY_ANOMALY_KEYS, "sceneDescription"],
	bolo: ["geminiModel"] as const,
};

/**
 * BOLO ("be on the look-out") pipeline params. Each BOLO instance describes
 * one subject (person or vehicle) to watch for; multiple instances per
 * installation are supported, distinguished by `label`.
 *
 * Label contract: for a BOLO instance created by a Watch, `label` IS the
 * owning `Watch._id` — Watch activation sets `label = watch.id`. That makes
 * the label the immutable join key that threads through
 * `Measurement.pipelineLabel` and `Event.watches[]` back to the Watch, with no
 * extra foreign key. Manually-created BOLO instances (raw pipeline config, not
 * via a Watch) may carry any string here and have no Watch record behind them.
 */
export interface BoloParams {
	ageRange?: { max?: number; min?: number };
	/** 1-10, severity threshold to emit a measurement */
	alertThreshold?: number;
	clothing?: string;
	/** Free-text description of who/what to look for (required) */
	description: string;
	geminiModel?: string;
	vehicleDescription?: string;
}

/**
 * Discriminated union of all pipeline instance variants. Stored as an array
 * on `VideoConfig.config.pipelines`. Merged across scopes by `(type, label)`
 * identity (sensor > zone > installation).
 */
export type PipelineInstance =
	| ({
			/**
			 * Required for BOLO. For Watch-created instances this is the owning
			 * `Watch._id` (the deliberate label-is-watch-id overload — see
			 * `BoloParams` and `PipelineInstanceBase.label`).
			 */
			label: string;
			params: BoloParams;
			type: "bolo";
	  } & PipelineInstanceBase)
	| (PipelineInstanceBase & {
			params: AnomalyParams;
			type: "anomaly";
	  });

interface PipelineInstanceBase {
	/** Set to `false` to disable an instance without removing it from config. */
	enabled?: boolean;
	/**
	 * Identity label distinguishing instances of the same type. Deliberately
	 * overloaded by type — do NOT assume it is always human-readable:
	 * - `bolo`: the owning `Watch._id` when created by a Watch (an opaque join
	 *   key, not a display name). Raw/manually-created BOLO instances may use
	 *   any string. See `BoloParams`.
	 * - `anomaly`: an optional human-readable eval-variant name (e.g. "variant-A").
	 */
	label?: string;
}

/**
 * Identity key used to merge pipeline instances across scopes.
 * Two instances with the same key are merged; different keys coexist.
 */
export function pipelineInstanceKey(instance: PipelineInstance): string {
	return `${instance.type}:${instance.label ?? ""}`;
}
